<template>
  <div class="card chart-card">
    <div class="card-header" v-if="$slots.header">
      <slot name="header"></slot>
    </div>

    <div class="card-body">
      <!-- Tooltip now lives INSIDE the chart element -->
      <div :id="chartId" ref="chartEl" class="ct-chart d-flex justify-content-center">
        <div
          v-show="tooltip.visible"
          class="ct-tooltip"
          :style="tooltip.style"
        >
          {{ tooltip.content }}
        </div>
      </div>
    </div>

    <div class="card-footer" v-if="$slots.footer">
      <slot name="footer"></slot>
    </div>
  </div>
</template>

<script>
export default {
  name: "chart-card",
  props: {
    chartType: { type: String, default: "Line" },
    chartOptions: { type: Object, default: () => ({}) },
    chartData: { type: Object, default: () => ({ labels: [], series: [] }) },
    responsiveOptions: [Object, Array],
  },
  data() {
    return {
      chartId: "no-id",
      $Chartist: null,
      chart: null,
      tooltip: {
        visible: false,
        content: "",
        style: { left: "0px", top: "0px" },
      },
    };
  },
  methods: {
    initChart() {
      const chartIdQuery = `#${this.chartId}`;
      this.chart = this.$Chartist[this.chartType](
        chartIdQuery,
        this.chartData,
        this.chartOptions,
        this.responsiveOptions
      );
      this.$emit("initialized", this.chart);

      if (this.chartType === "Line") this.animateLineChart();
      if (this.chartType === "Bar") this.animateBarChart();

      if (this.chartType === "Line") this.attachLineTooltips();
    },

    attachLineTooltips() {
      const chartEl = this.$refs.chartEl;

      const positionTooltip = (e) => {
        // Measure relative to the chart element itself
        const rect = chartEl.getBoundingClientRect();
        this.tooltip.style = {
          position: "absolute",
          left: `${e.clientX - rect.left + 10}px`,  // +10px to the right of cursor
          top: `${e.clientY - rect.top - 16}px`,    // 16px above cursor
        };
      };

      this.chart.on("draw", (data) => {
        // --- Points: "Member — Label: value"
        if (data.type === "point") {
          const seriesName =
            (data.series && data.series.name) || `Series ${data.seriesIndex + 1}`;

          const label =
            Array.isArray(this.chartData.labels) &&
            this.chartData.labels[data.index] !== undefined
              ? this.chartData.labels[data.index]
              : null;

          const text = seriesName + (label ? ` — ${label}` : "") + `: ${data.value.y}`;

          const node = data.element._node;
          node.style.pointerEvents = "all";
          node.style.cursor = "pointer";

          node.addEventListener("mouseenter", (e) => {
            this.tooltip.content = text;
            this.tooltip.visible = true;
            positionTooltip(e);
          });
          node.addEventListener("mousemove", positionTooltip);
          node.addEventListener("mouseleave", () => (this.tooltip.visible = false));
        }

        // --- Lines: show member name on hover
        if (data.type === "line") {
          const seriesName =
            (data.series && data.series.name) || `Series ${data.seriesIndex + 1}`;
          const node = data.element._node;
          node.style.pointerEvents = "all";

          node.addEventListener("mouseenter", (e) => {
            this.tooltip.content = seriesName;
            this.tooltip.visible = true;
            positionTooltip(e);
          });
          node.addEventListener("mousemove", positionTooltip);
          node.addEventListener("mouseleave", () => (this.tooltip.visible = false));
        }
      });
    },

    updateChartId() {
      const currentTime = new Date().getTime().toString();
      const randomInt = this.getRandomInt(0, currentTime);
      this.chartId = `div_${randomInt}`;
    },
    getRandomInt(min, max) {
      return Math.floor(Math.random() * (max - min + 1)) + Number(min);
    },

    animateLineChart() {
      let seq = 0;
      const durations = 600;
      const delays = 80;

      this.chart.on("draw", (data) => {
        if (data.type === "line" || data.type === "area") {
          data.element.animate({
            d: {
              begin: 600,
              dur: 700,
              from: data.path
                .clone()
                .scale(1, 0)
                .translate(0, data.chartRect.height())
                .stringify(),
              to: data.path.clone().stringify(),
              easing: this.$Chartist.Svg.Easing.easeOutQuint,
            },
          });
        } else if (data.type === "point") {
          seq++;
          data.element.animate({
            opacity: {
              begin: seq * delays,
              dur: durations,
              from: 0,
              to: 1,
              easing: "ease",
            },
          });
        }
      });
      seq = 0;
    },

    animateBarChart() {
      let seq = 0;
      const durations = 500;
      const delays = 80;
      this.chart.on("draw", (data) => {
        if (data.type === "bar") {
          seq++;
          data.element.animate({
            opacity: {
              begin: seq * delays,
              dur: durations,
              from: 0,
              to: 1,
              easing: "ease",
            },
          });
        }
      });
    },
  },

  async mounted() {
    this.updateChartId();
    const Chartist = await import("chartist");
    this.$Chartist = Chartist.default ? Chartist.default : Chartist;
    this.initChart();
      this.chart.on('created', () => {
      (this.members || []).forEach((m, i) => {
        const seriesEl = this.$refs.chartEl.querySelector(`.ct-series.member-${m.id}`);
        if (seriesEl) {
          seriesEl.querySelectorAll('.ct-line,.ct-point').forEach(el => {
            el.style.stroke = m.color;
          });
        }
      });
    });
    this.chart.on("created", () => {
      // loop series and set stroke color = member color
      (this.chartData.series || []).forEach((series, i) => {
        const member = this.$parent.members[i]; // parent holds colors
        if (!member) return;
        const color = member.color;

        const seriesEl = this.$refs.chartEl.querySelector(`.ct-series.member-${i}`);
        if (seriesEl) {
          seriesEl.querySelectorAll(".ct-line, .ct-point").forEach(el => {
            el.style.stroke = color;
          });
        }
      });
    });


  },
};
</script>

<style>
.ct-chart {
  position: relative; /* tooltip is absolutely positioned inside here */
  height: auto !important;
  display: flex;
}

/* Make points easier to hover */
.ct-point {
  stroke-width: 8px;
  pointer-events: all;
}

/* Optional: improve hover target for lines */
.ct-series .ct-line {
  stroke-width: 3px;
}

/* Tooltip style */
.ct-tooltip {
  position: absolute;
  padding: 6px 8px;
  font-size: 12px;
  background: rgba(0, 0, 0, 0.78);
  color: #fff;
  border-radius: 4px;
  pointer-events: none;
  white-space: nowrap;
  /* Remove transform so left/top are literal pixel offsets */
  transform: none;
}
</style>
